import { useState } from 'react';
import { motion } from 'framer-motion';

type View = 'b2c' | 'b2b' | 'remesas';

interface MetricRow {
  label: string;
  value: string;
  bar: number; // 0-100 for visual width
  color: string;
  description: string;
}

const views: Record<View, { title: string; rows: MetricRow[]; kpis: { label: string; value: string; color: string; desc: string }[] }> = {
  b2c: {
    title: 'Suscriptores — Lo que gana Migro por cada cliente que se suscribe',
    rows: [
      { label: 'LTV del suscriptor (10 meses)', value: '580 EUR', bar: 100, color: 'bg-emerald-500', description: '58 EUR/mes x 10 meses de suscripcion' },
      { label: 'Suscripcion mensual media', value: '58 EUR/mes', bar: 10, color: 'bg-emerald-400', description: 'Rango: 48 EUR (grado A/B) a 68 EUR (grado C)' },
      { label: 'Coste captar al suscriptor', value: '33 EUR', bar: 6, color: 'bg-orange-500', description: '2 EUR por lead / 6% conversion = 33 EUR por cliente' },
      { label: 'Coste por contacto', value: '2 EUR', bar: 1, color: 'bg-orange-400', description: 'Meta Ads a 2 EUR/lead — la industria paga 5-15 EUR' },
    ],
    kpis: [
      { label: 'LTV vs Coste de captacion', value: '17x', color: 'text-emerald-400', desc: 'Gastamos 33 EUR, el cliente genera 580 EUR' },
      { label: 'Margen de beneficio', value: '70%', color: 'text-emerald-400', desc: 'De cada 58 EUR/mes, nos quedamos ~40 EUR' },
      { label: 'Primer cobro', value: 'Dia 1', color: 'text-blue-400', desc: 'Se cobra la primera cuota al momento de contratar' },
      { label: 'De cada 100 contactos', value: '6 se suscriben', color: 'text-purple-400', desc: 'Tasa real validada con datos de marzo 2026' },
    ],
  },
  b2b: {
    title: 'Abogados Suscriptores — Lo que paga cada abogado al mes',
    rows: [
      { label: 'Plan Despacho', value: '1,796 EUR/mes', bar: 100, color: 'bg-purple-500', description: '50 clientes cualificados por semana' },
      { label: 'Plan Independiente', value: '896 EUR/mes', bar: 50, color: 'bg-blue-500', description: '20 clientes cualificados por semana' },
      { label: 'Plan Emprendedor', value: '537 EUR/mes', bar: 30, color: 'bg-emerald-500', description: '10 clientes cualificados por semana' },
      { label: 'Coste captar al abogado', value: '300 EUR', bar: 17, color: 'bg-orange-500', description: 'Se recupera en el primer mes' },
    ],
    kpis: [
      { label: 'Retorno para Migro', value: '43x', color: 'text-emerald-400', desc: 'Gastamos 300 EUR captandolo, ingresamos 12,900 EUR/ano' },
      { label: 'Beneficio sobre cada cuota', value: '75-80%', color: 'text-emerald-400', desc: 'Margen muy alto porque la plataforma ya esta construida' },
      { label: 'Ingreso anual por despacho', value: '21,552 EUR', color: 'text-purple-400', desc: 'Un despacho top genera esto al ano' },
      { label: 'Lo que gana el abogado', value: '12-18x', color: 'text-blue-400', desc: 'Factura 12-18 veces mas de lo que paga a Migro' },
    ],
  },
  remesas: {
    title: 'Envios de Dinero — Lo que gana Migro por cada transferencia',
    rows: [
      { label: 'Ingreso por envio (500 EUR)', value: '~5 EUR', bar: 100, color: 'bg-blue-500', description: 'Comision + margen en tipo de cambio' },
      { label: 'Comision fija', value: '2.25 EUR', bar: 45, color: 'bg-blue-400', description: 'Cobro fijo por cada transferencia' },
      { label: 'Beneficio neto', value: '1.70 EUR', bar: 34, color: 'bg-emerald-500', description: 'Despues de pagar la red blockchain' },
      { label: 'Coste de la transferencia', value: '0.01 EUR', bar: 1, color: 'bg-orange-500', description: 'Practicamente gratis gracias a blockchain' },
    ],
    kpis: [
      { label: 'Beneficio por cada envio', value: '~34%', color: 'text-emerald-400', desc: 'De 5 EUR, nos quedamos 1.70 EUR' },
      { label: 'El cliente envia dinero', value: 'Cada mes', color: 'text-blue-400', desc: 'Es ingreso recurrente garantizado' },
      { label: 'Con 10,000 usuarios', value: '30,000 EUR/mes', color: 'text-purple-400', desc: 'Solo por envios de dinero' },
      { label: 'Vs Western Union', value: '60-75% mas barato', color: 'text-orange-400', desc: 'El migrante ahorra y Migro gana' },
    ],
  },
};

export function UnitEconomicsChart() {
  const [view, setView] = useState<View>('b2c');
  const { title, rows, kpis } = views[view];

  return (
    <section id="economics" className="py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5 }}
          className="mb-10 text-center"
        >
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-emerald-400">
            Los numeros
          </span>
          <h2 className="font-display text-3xl font-bold text-white sm:text-4xl">
            Cuanto{' '}
            <span className="bg-gradient-to-r from-emerald-400 to-emerald-500 bg-clip-text text-transparent">ganamos</span>
            {' '}en cada linea
          </h2>
        </motion.div>

        {/* Toggle */}
        <div className="mb-8 flex justify-center gap-2">
          {([
            { key: 'b2c' as View, label: 'Clientes Directos' },
            { key: 'b2b' as View, label: 'Abogados' },
            { key: 'remesas' as View, label: 'Envio de Dinero' },
          ]).map(v => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                view === v.key
                  ? 'bg-emerald-500/20 text-emerald-400 ring-1 ring-emerald-400/30'
                  : 'text-slate-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Bar rows */}
          <motion.div
            key={view}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.35 }}
            className="rounded-2xl border border-white/10 bg-white/[0.06] p-6 backdrop-blur-md lg:col-span-2"
          >
            <h3 className="mb-5 text-sm font-semibold text-slate-300">{title}</h3>
            <div className="space-y-4">
              {rows.map((row, i) => (
                <motion.div
                  key={row.label}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <div className="mb-1.5 flex items-baseline justify-between">
                    <span className="text-sm text-slate-200">{row.label}</span>
                    <span className="text-sm font-bold text-white">{row.value}</span>
                  </div>
                  <div className="relative h-7 w-full overflow-hidden rounded-lg bg-white/[0.04]">
                    <motion.div
                      className={`absolute inset-y-0 left-0 rounded-lg ${row.color}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${row.bar}%` }}
                      transition={{ duration: 0.8, delay: 0.1 * i, ease: 'easeOut' }}
                    />
                    <span className="absolute inset-y-0 left-3 flex items-center text-[11px] text-white/70">
                      {row.description}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* KPIs */}
          <motion.div
            key={`kpi-${view}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: 0.1 }}
            className="space-y-3"
          >
            {kpis.map((kpi, i) => (
              <div
                key={i}
                className="rounded-xl border border-white/10 bg-white/[0.06] p-4 backdrop-blur-md"
              >
                <p className="text-[11px] uppercase tracking-wider text-slate-500">{kpi.label}</p>
                <p className={`mt-1 text-2xl font-bold ${kpi.color}`}>{kpi.value}</p>
                <p className="mt-0.5 text-[11px] text-slate-500">{kpi.desc}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
